import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <a href="/" className="flex items-center gap-2 whitespace-nowrap">
                <span className="font-heading text-xl font-bold text-foreground-950">OOPBUY</span>
                <span className="font-body text-xs text-foreground-600 hidden sm:inline">Spreadsheet</span>
              </a>

              <div className="hidden md:flex items-center gap-6">
                <a href="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Startseite</a>
                <div ref={dropdownRef} className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
                  >
                    Kategorien
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg py-2 border border-background-200/70 max-h-[480px] overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <a href="/einkaufsfuehrer" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Einkaufsführer</a>
                <a href="/was-ist-oopbuy-spreadsheet" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Guide</a>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                OOPBUY Spreadsheet
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-950 cursor-pointer"
                aria-label="Menü öffnen"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-0 right-0 w-[300px] max-w-[85vw] h-full bg-background-50 overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200/60">
              <span className="font-heading text-lg font-bold text-foreground-950">OOPBUY Spreadsheet</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
                aria-label="Menü schließen"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="py-4">
              <a
                href="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-6 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Startseite
              </a>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-6 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  Kategorien
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="bg-background-100/70 py-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-8 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-200/50 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <a
                href="/einkaufsfuehrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-6 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Einkaufsführer
              </a>

              <a
                href="/was-ist-oopbuy-spreadsheet"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-6 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-guide-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Guide
              </a>

              <div className="border-t border-background-200/60 mt-3 pt-3 px-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  OOPBUY Spreadsheet
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}